import { get } from 'lodash'
import {
  ComponentProps,
  ComponentType,
  createElement,
  ElementType,
  forwardRef,
  useEffect,
} from 'react'
import { ForwardRef } from 'react-is'

type JSXComponent = ElementType

const isForwardRef = (Component?: ComponentType) =>
  get(Component, ['$$typeof']) === ForwardRef

export interface ComponentMapper<T extends JSXComponent> {
  (target: T): JSXComponent
}

export const mapReadComponent =
  <C extends JSXComponent, T extends JSXComponent>(component: C) =>
  (target: T) =>
  ({ readonly, ...props }: ComponentProps<T> & { readonly?: boolean }) =>
    createElement(readonly ? component : target, props)

export const wrapMountAndUnMount = <T extends JSXComponent>(target: T) =>
  forwardRef(({ onMount, onUnMount, ...props }: ComponentProps<T>, ref) => {
    useEffect(() => {
      onMount?.()
      return onUnMount
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return createElement(target, {
      ...props,
      ref,
    })
  })

export const connect = <T extends JSXComponent>(
  target: T,
  ...args: ComponentMapper<T>[]
) => {
  const Target = args.reduce<T>((acc, mapper) => {
    acc = mapper(target) as T
    return acc
  }, target)

  if (isForwardRef(target as ComponentType)) {
    return forwardRef((props, ref) =>
      createElement(Target, {
        ...props,
        ref,
      })
    )
  }

  return (props: ComponentProps<T>) => createElement(Target, props)
}

export const connectMountAndUnMound = <T extends JSXComponent>(target: T) =>
  connect(target, wrapMountAndUnMount) as T

export default connect
